<script setup>
import { computed, ref } from 'vue';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import { getQuantileIntervals } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // ['yyyy-MM-dd'] oldest → newest; every day renders even with no data
  days: { type: Array, default: () => [] },
  // { 'yyyy-MM-dd|hour': { value: Number, tooltip: String } }
  cells: { type: Object, default: () => ({}) },
  mini: { type: Boolean, default: false },
});

const { t } = useI18n();

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const quantileRange = computed(() => {
  const values = Object.values(props.cells).map(cell => cell.value);
  return getQuantileIntervals(values, [0.2, 0.4, 0.6, 0.8, 0.9, 0.99]);
});

const COLOR_CLASSES = [
  'bg-n-blue-3 border border-n-blue-4/30',
  'bg-n-blue-5 border border-n-blue-6/30',
  'bg-n-blue-7 border border-n-blue-8/30',
  'bg-n-blue-8 border border-n-blue-9/30',
  'bg-n-blue-10 border border-n-blue-8/30',
  'bg-n-blue-11 border border-n-blue-10/30',
];
const EMPTY_CLASS =
  'border border-n-container bg-n-slate-2 dark:bg-n-slate-1/30';

function cellFor(day, hour) {
  return props.cells[`${day}|${hour}`];
}

function cellClass(day, hour) {
  const value = cellFor(day, hour)?.value || 0;
  if (!value) return EMPTY_CLASS;
  let level = [...quantileRange.value, Infinity].findIndex(
    range => value <= range && value > 0
  );
  if (level > 6) level = 5;
  if (level === 0) return EMPTY_CLASS;
  return COLOR_CLASSES[level - 1];
}

const DAYS_OF_WEEK = [
  t('DAYS_OF_WEEK.SUNDAY'),
  t('DAYS_OF_WEEK.MONDAY'),
  t('DAYS_OF_WEEK.TUESDAY'),
  t('DAYS_OF_WEEK.WEDNESDAY'),
  t('DAYS_OF_WEEK.THURSDAY'),
  t('DAYS_OF_WEEK.FRIDAY'),
  t('DAYS_OF_WEEK.SATURDAY'),
];

function dayLabel(day) {
  return DAYS_OF_WEEK[getDay(new Date(day))];
}

function dateLabel(day) {
  return format(new Date(day), 'MMM d');
}

const tooltip = ref({ visible: false, x: 0, y: 0, text: '' });

function showTooltip(event, day, hour) {
  const cell = cellFor(day, hour);
  const base = `${dayLabel(day)}, ${dateLabel(day)} · ${hour}:00`;
  tooltip.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY,
    text: cell ? `${base} — ${cell.tooltip}` : base,
  };
}

function hideTooltip() {
  tooltip.value.visible = false;
}
</script>

<template>
  <div
    class="relative grid w-full gap-x-3 gap-y-1.5"
    :class="mini ? 'grid-cols-[56px_1fr]' : 'grid-cols-[80px_1fr]'"
  >
    <div class="grid gap-[4px] flex-shrink-0">
      <div
        v-for="day in days"
        :key="day"
        class="flex justify-end"
        :class="
          mini
            ? 'h-4 items-center text-[9px] text-n-slate-11'
            : 'h-8 flex-col items-end justify-center text-[10px] font-semibold text-n-slate-12'
        "
      >
        <template v-if="mini">{{ dateLabel(day) }}</template>
        <template v-else>
          {{ dayLabel(day) }}
          <time class="font-normal text-n-slate-11">{{ dateLabel(day) }}</time>
        </template>
      </div>
    </div>
    <div class="grid gap-[4px] w-full" :class="mini ? '' : 'min-w-[700px]'">
      <div
        v-for="day in days"
        :key="day"
        class="grid gap-[4px] grid-cols-[repeat(24,_1fr)]"
      >
        <div
          v-for="hour in HOURS"
          :key="hour"
          class="rounded-sm cursor-pointer"
          :class="[mini ? 'h-4' : 'h-8', cellClass(day, hour)]"
          @mouseenter="showTooltip($event, day, hour)"
          @mousemove="showTooltip($event, day, hour)"
          @mouseleave="hideTooltip"
        />
      </div>
    </div>
    <div />
    <div
      class="grid grid-cols-[repeat(24,_1fr)] gap-[4px] w-full font-semibold text-n-slate-11"
      :class="mini ? 'text-[7px] h-3' : 'text-[8px] h-5'"
    >
      <div
        v-for="hour in HOURS"
        :key="hour"
        class="flex items-center justify-center"
      >
        {{ hour }}
      </div>
    </div>
    <div
      v-if="tooltip.visible"
      class="fixed z-50 pointer-events-none rounded-lg bg-n-solid-3 outline outline-1 outline-n-container px-3 py-1.5 text-xs text-n-slate-12 shadow-lg"
      :style="{ left: `${tooltip.x + 12}px`, top: `${tooltip.y + 12}px` }"
    >
      {{ tooltip.text }}
    </div>
  </div>
</template>
